import React, { useState } from "react";
import { Star } from "lucide-react";
import { api } from "../utils/api";

/**
 * Post-closure feedback widget. Shows stars for rating and optional comment.
 * Only visible to the requester on closed/completed tickets.
 */
export default function FeedbackWidget({ kind, ticket, currentUser, onSubmitted }) {
  const owner = ticket.reporter_id || ticket.requester_id;
  const isOwner = owner && currentUser && owner === currentUser.id;
  const closedSet = {
    incidents: ["resolved", "closed"],
    "service-requests": ["completed", "closed"],
    "change-requests": ["completed", "implemented"],
    problems: ["resolved", "closed"],
  };
  const isClosed = closedSet[kind]?.includes(ticket.status);

  const [rating, setRating] = useState(ticket.feedback_rating || 0);
  const [comment, setComment] = useState(ticket.feedback_comment || "");
  const [submitted, setSubmitted] = useState(!!ticket.feedback_submitted_at);
  const [hover, setHover] = useState(0);

  if (!isOwner || !isClosed) return null;

  async function submit() {
    if (rating < 1) { alert("Please pick a rating"); return; }
    try {
      await api.post(`/api/${kind}/${ticket.id}/feedback`, { rating, comment: comment || null });
      setSubmitted(true);
      onSubmitted?.(rating);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit feedback");
    }
  }

  return (
    <div className="card p-5">
      <h2 className="font-semibold mb-2">How did we do?</h2>
      {submitted ? (
        <div>
          <div className="flex gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={20} fill={n <= (ticket.feedback_rating || rating) ? "#F59E0B" : "none"} stroke={n <= (ticket.feedback_rating || rating) ? "#F59E0B" : "#cbd5e1"} />
            ))}
          </div>
          <p className="text-sm text-gray-600">Thanks for the feedback!</p>
          {(ticket.feedback_comment || comment) && (
            <p className="text-sm text-gray-700 mt-2 italic">"{ticket.feedback_comment || comment}"</p>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-3">Please rate your experience and leave any comments.</p>
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} className="cursor-pointer">
                <Star size={28} fill={n <= (hover || rating) ? "#F59E0B" : "none"} stroke={n <= (hover || rating) ? "#F59E0B" : "#cbd5e1"} />
              </button>
            ))}
          </div>
          <textarea className="input" rows="2" placeholder="Optional comment…" value={comment} onChange={(e) => setComment(e.target.value)} />
          <button onClick={submit} className="btn-primary mt-2">Submit feedback</button>
        </>
      )}
    </div>
  );
}
